import type { Reaction } from '@adv-frontend/reactions';

import type { BaseField, FieldInstance } from './types';

declare const $requiredField: unique symbol;

/**
 * Инстанс поля для типизации переиспользуемых реакций.
 *
 * Реакцию с этим типом нужно передавать через `createReusableReaction()`,
 * который проверяет совместимость декларации поля на уровне типов.
 *
 * @typeParam RequiredField — декларация поля, требуемая реакцией
 *
 * @example
 * ```typescript
 * const trimReaction = createReaction({
 *     *data(instance: ReactionFieldInstance<PrimitiveField<string>>) {
 *         return instance.getValue();
 *     },
 *     effect(value, { param }) {
 *         param.setValue(value.trim());
 *     },
 * });
 *
 * const titleField = createPrimitiveField({
 *     defaultValue: '',
 *     reactions: (createReactions, createReusableReaction) =>
 *         createReactions([createReusableReaction(trimReaction)]),
 * });
 * ```
 */
export type ReactionFieldInstance<RequiredField extends BaseField> = FieldInstance<RequiredField> & {
    readonly [$requiredField]?: RequiredField;
};

type ExtractReusableReactionField<ReusableReaction> =
    ReusableReaction extends Reaction<infer _ReactionData, infer ReactionInstance>
        ? ReactionInstance extends ReactionFieldInstance<infer RequiredField extends BaseField>
            ? RequiredField
            : never
        : never;

export type CompatibleReusableFieldReaction<CurrentField extends BaseField, ReusableReaction> =
    ExtractReusableReactionField<ReusableReaction> extends infer RequiredField extends BaseField
        ? CurrentField extends RequiredField
            ? RequiredField extends CurrentField
                ? ReusableReaction
                : never
            : never
        : never;

/** Создаёт реакцию, совместимую с декларацией конкретного поля. */
export type CreateReusableFieldReactionApi<CurrentField extends BaseField, CurrentFieldReaction> = <ReusableReaction>(
    reaction: ReusableReaction &
        (ReusableReaction extends CompatibleReusableFieldReaction<CurrentField, ReusableReaction> ? unknown : never)
) => CurrentFieldReaction;

/** Создаёт runtime identity-функцию с публичной сигнатурой регистрации переиспользуемой реакции. */
export function createReusableReactionApi<ReactionApi>(): ReactionApi;
export function createReusableReactionApi(): unknown {
    return (reaction: unknown): unknown => reaction;
}
